import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert, Typography, LinearProgress } from '@mui/material';
import tagTasksService from '../services/tagTasksService';
import { useNavigate } from 'react-router-dom';

/**
 * Component that checks localStorage for pending tag extraction tasks
 * and displays their status
 */
const TagExtractionStatus = ({ itemId }) => {
  const [taskStatus, setTaskStatus] = useState(null);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const navigate = useNavigate();

  // Check for any stored tag extraction tasks on mount
  // Function to poll task status until completion
  const pollTaskStatus = useCallback(async (taskId) => {
    try {
      const result = await tagTasksService.getTaskStatus(taskId);
      setTaskStatus(result);
      
      // If task is still running, continue polling
      if (result.status === 'pending' || result.status === 'running') {
        setTimeout(() => pollTaskStatus(taskId), 2000);
      } else {
        // Task completed or failed
        setIsPolling(false);
        
        if (result.status === 'completed') {
          console.log('Tag extraction completed successfully:', result);
          
          // After 3 seconds, remove the task data and hide the status
          setTimeout(() => {
            localStorage.removeItem('tag_extraction_task');
            setTaskStatus(null);
            
            // Force reload to show the newly extracted tags
            navigate(0);
          }, 3000);
        } else {
          console.error('Tag extraction failed:', result.error);
          // Leave error message visible for 5 seconds before removing
          setTimeout(() => {
            localStorage.removeItem('tag_extraction_task');
            setTaskStatus(null);
          }, 5000);
        }
      }
    } catch (err) {
      setError(`Error checking task status: ${err.message}`);
      setIsPolling(false);
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setError(null);
        localStorage.removeItem('tag_extraction_task');
      }, 5000);
    }
  }, [navigate]);
  useEffect(() => {
    const checkStoredTask = () => {
      try {
        const storedTaskData = localStorage.getItem('tag_extraction_task');
        if (!storedTaskData) return null;
        
        const taskData = JSON.parse(storedTaskData);
        
        // Only process if this task belongs to the current item and is less than 10 minutes old
        if (taskData && 
            taskData.item_id === itemId && 
            taskData.timestamp > (Date.now() - 10 * 60 * 1000)) {
          return taskData;
        }
        
        // Clear old task data
        localStorage.removeItem('tag_extraction_task');
        return null;
      } catch (err) {
        console.error('Error parsing stored tag extraction task:', err);
        localStorage.removeItem('tag_extraction_task');
        return null;
      }
    };

    const storedTask = checkStoredTask();
    if (storedTask && storedTask.task_id) {
      // Start polling for task status
      setIsPolling(true);
      pollTaskStatus(storedTask.task_id);
    }
  }, [itemId, pollTaskStatus]);


  // No task running or completed, don't render anything
  if (!taskStatus && !error && !isPolling) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        width: 'auto',
        maxWidth: '90%'
      }}
    >
      {error ? (
        <Alert 
          severity="error"
          variant="filled"
          sx={{
            boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderRadius: 2
          }}
        >
          {error}
        </Alert>
      ) : taskStatus ? (
        <Alert 
          severity={taskStatus.status === 'completed' ? 'success' : 
                   taskStatus.status === 'failed' ? 'error' : 'info'}
          variant="filled"
          sx={{
            boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderRadius: 2
          }}
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {taskStatus.status === 'completed' ? 'Tags extracted successfully!' : 
               taskStatus.status === 'failed' ? `Tag extraction failed: ${taskStatus.error || 'Unknown error'}` :
               'Extracting tags from image...'}
            </Typography>
            
            {(taskStatus.status === 'pending' || taskStatus.status === 'running') && (
              <LinearProgress />
            )}
          </Box>
        </Alert>
      ) : isPolling ? (
        <Alert 
          severity="info"
          variant="filled"
          sx={{
            boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderRadius: 2
          }}
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Checking tag extraction status...
            </Typography>
            <LinearProgress />
          </Box>
        </Alert>
      ) : null}
    </Box>
  );
};

export default TagExtractionStatus;
